import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, Checkbox, CircularProgress, Collapse, IconButton, MenuItem, Paper, TextField, Tooltip, Typography } from '@mui/material'
import Page from '../../components/molecules/page'
import { Air, ArrowDownward, ArrowUpward, ChangeCircle, CheckCircle, Delete, DragIndicator, HeatPump, InfoOutlined, Lock, Save, SquareFoot, SvgIconComponent, WarningAmberOutlined, WaterDrop } from '@mui/icons-material'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../state/configureStore'
import { fetchCalibrationStatus, pushRecipe, saveRecipe } from '../../state/thunks/machines'
import { LoadingButton } from '@mui/lab'
import { useParams } from 'react-router-dom'
import { FetchFromBackoffice } from '../../utils/request'
import { DateTime } from 'luxon'
import { estimateRecipeRuntime, RuntimeEstimatorConfig } from './estimateRecipeRuntime'

export type BlockType = 
  | 'heaters-start'
  | 'spray-setup'
  | 'spray-swipe'
  | 'spray-rotate'
  | 'spray-finilize'
  | 'fan-start'
  | 'fan-stop'
  | 'rotate'
  | 'sleep' 

type PositionSetting = {
  key: 'Position',
  value: 'Toe' | 'Right Toe' | 'Right' | 'Right Heel' | 'Heel' | 'Left Heel' | 'Left' | 'Left Toe'
}

const positionLookup: Record<PositionSetting['value'], number> = {
  'Toe': 0,
  'Right Toe': 2,
  'Right': 4,
  'Right Heel': 6,
  'Heel': 8,
  'Left Heel': 10,
  'Left': 12,
  'Left Toe': 15
}

export type RecipeSetting =
  | PositionSetting
  | { key: 'Speed', value: number | null }
  | { key: 'TimeBetweenHeaters', value: number | null }
  | { key: 'TimeToRunHeaters', value: number | null }
  | { key: 'Swipes', value: number | null }
  | { key: 'TimeForServoToTurnOneDegree', value: number | null }
  | { key: 'SwipeAnglePos', value: number | null }
  | { key: 'SwipeAngleNeg', value: number | null }
  | { key: 'Laps', value: number | null }
  | { key: 'Clockwise', value: boolean }
  | { key: 'Angle', value: number | null }
  | { key: 'Time', value: number | null }

const recipeSettingDetails: Record<RecipeSetting['key'], { title: string; explanation: string, max: number | undefined, min: number | undefined}> = {
  Position: { title: 'Position', explanation: 'The position of the nozzle relative to the shoes.', max: 7, min: 0 },
  Speed: { title: 'Speed', explanation: 'The operating speed (max 7 for rotating the plate and max 3 for rotating the fan).', max: 7, min: 0 },
  TimeBetweenHeaters: { title: 'Heater swap time', explanation: 'The heaters run in sequence to not get overheated. This is the time for ', max: 80, min: 0 }, 
  TimeToRunHeaters: { title: 'Heater run time', explanation: 'The duration each heater remains active in seconds.', max: 60, min: 0 },
  Swipes: { title: 'Swipes', explanation: 'How many times the nozzle will sweep back and forth.', max: 10, min: 0 },
  TimeForServoToTurnOneDegree: { title: 'Swipe duration', explanation: 'Total time in seconds that one swipe will last.', max: 60, min: 0 }, 
  SwipeAnglePos: { title: 'Left spray reach', explanation: 'How far left the nozzle should spray (0° to 60°). Left is from the perspective of the nozzle to the middle of the plate.', max: 60, min: 0 },
  SwipeAngleNeg: { title: 'Right spray reach', explanation: 'How far right the nozzle should spray (0° to 60°). Right is from the perspective of the nozzle to the middle of the plate.', max: 60, min: 0 },
  Laps: { title: 'Laps', explanation: 'The number of full plate rotations.', max: 10, min: 0 },
  Clockwise: { title: 'Clockwise', explanation: 'Set to true to rotate the plate clockwise; otherwise, it rotates counterclockwise.', max: undefined, min: undefined },
  Angle: { title: 'Nozzle angle', explanation: 'The spray nozzle angle.', max: 60, min: 0 },
  Time: { title: 'Duration', explanation: 'The plate rotation time, in seconds.', max: 60, min: 0 }
}

type LibraryBlock = {
  type: BlockType,
  title: string,
  description: string,
  icon: SvgIconComponent,
  configuration: RecipeSetting[],
}

export type RecipeStep = {
  id: string,
  type: BlockType,
  title: string,
  description: string,
  icon: SvgIconComponent,
  configuration: RecipeSetting[]
}

type DragPayload =
  | { source: 'library', type: BlockType }
  | { source: 'recipe', stepId: string }

type RecipeInstructionResponse = {
  id: string
  [key: string]: string | number | boolean | undefined
}

type RecipeInstructionsResponse = {
  id: string
  name: string
  instructions: RecipeInstructionResponse[]
}

const libraryBlocks: LibraryBlock[] = [
  {
    type: 'heaters-start',
    title: 'Start heater',
    description: 'Starting the heater',
    icon: HeatPump,
    configuration: [
      { key: 'TimeBetweenHeaters', value: 13000 },
      { key: 'TimeToRunHeaters', value: 27000 }
    ],
  },
  {
    type: 'spray-rotate',
    title: 'Rotate plate while spraying',
    description: 'Rotating the plate while spraying',
    icon: WaterDrop,
    configuration: [
      { key: 'Speed', value: 3 },
      { key: 'Laps', value: 1 },
      { key: 'Clockwise', value: false },
      { key: 'Angle', value: 0 }
    ],
  },
  {
    type: 'fan-start',
    title: 'Start fan',
    description: 'Starting the fan',
    icon: Air,
    configuration: [
      { key: 'Speed', value: 3 }
    ],
  },
  {
    type: 'spray-swipe',
    title: 'Spray on position',
    description: 'Spray on a custom position',
    icon: WaterDrop,
    configuration: [
      { key: 'Position', value: 'Toe' },
      { key: 'Swipes', value: 2 },
      { key: 'TimeForServoToTurnOneDegree', value: 10 },
      { key: 'SwipeAnglePos', value: 45 },
      { key: 'SwipeAngleNeg', value: -45 }
    ],
  },
  {
    type: 'rotate',
    title: 'Rotate',
    description: 'Rotate the plate',
    icon: ChangeCircle,
    configuration: [
      { key: 'Speed', value: 2 },
      { key: 'Time', value: 10000 }
    ],
  },
]

const styling = {
  white: '#FFFFFF',
  secondaryColor: '#0000008C',
  gray: '#0000003B'
}

const RecipeLab = (): React.ReactElement => {
  const { recipeId } = useParams<{ recipeId: string }>()
  const isEditingExistingRecipe = Boolean(recipeId && recipeId.toLowerCase() !== 'new')

  const [addedRecipes, setAddedRecipes] = useState<RecipeStep[]>([])
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [recipeName, setRecipeName] = useState<string | null>(null)
  const [serialNumber, setSerialNumber] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [isPushing, setIsPushing] = useState<boolean>(false)
  const [isPushingCalibration, setIsPushingCalibration] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [saved, setSaved] = useState<boolean>(false)
  const [isFetchingRecipe, setIsFetchingRecipe] = useState<boolean>(false)
  const [pushedCalibration, setPushedCalibration] = useState<boolean>(false)
  const [calibrationSerialNumber, setCalibrationSerialNumber] = useState<string | null>(null)
  const [calibrationArticleNumber, setCalibrationArticleNumber] = useState<string | null>(null)
  const [calibrationPushedAt, setCalibrationPushedAt] = useState<string | null>(null)
  const [isFetchingCalibrationStatus, setIsFetchingCalibrationStatus] = useState<boolean>(false)
  const [calibratedRecipeDuration, setCalibratedRecipeDuration] = useState<number>(39)
  const [unpinnedHeater, setUnpinnedHeater] = useState<boolean>(false)

  const positionValues: PositionSetting['value'][] = ['Toe', 'Right Toe', 'Right', 'Right Heel', 'Heel', 'Left Heel', 'Left', 'Left Toe']
  const positionIndexLookup = Object.fromEntries(
    Object.entries(positionLookup).map(([position, index]) => [index, position])
  ) as Record<number, PositionSetting['value']>

  const dispatch = useDispatch<AppDispatch>()

  const createRecipeStep = (type: BlockType): RecipeStep => ({
    id: `${type}-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    type,
    title: libraryBlocks.find(block => block.type === type)?.title ?? 'Unknown title',
    description: libraryBlocks.find(block => block.type === type)?.description ?? 'Unknown description',
    icon: libraryBlocks.find(block => block.type === type)?.icon ?? HeatPump,
    configuration: libraryBlocks.find(block => block.type === type)?.configuration.map(setting => ({ ...setting })) ?? []
  })

  const parseInstructionToRecipeStep = (instruction: RecipeInstructionResponse): RecipeStep | null => {
    const instructionId = instruction.id as BlockType
    const block = libraryBlocks.find(candidate => candidate.type === instructionId)

    if (!block) {
      return null
    }

    const step = createRecipeStep(instructionId)

    const instructionFieldBySettingKey: Record<Exclude<RecipeSetting['key'], 'Position' | 'Clockwise'>, string> = {
      Speed: 'speed',
      TimeBetweenHeaters: 'timeBetweenHeaters',
      TimeToRunHeaters: 'timeToRunHeaters',
      Swipes: 'swipes',
      TimeForServoToTurnOneDegree: 'timeForServoToTurnOneDegree',
      SwipeAnglePos: 'swipeAnglePos',
      SwipeAngleNeg: 'swipeAngleNeg',
      Laps: 'laps',
      Angle: 'angle',
      Time: 'time',
    }

    const configuration = step.configuration.map(setting => {
      if (setting.key === 'Position') {
        const rawPosition = instruction.position
        if (typeof rawPosition === 'number') {
          const foundPosition = positionIndexLookup[rawPosition]
          if (foundPosition) {
            return { ...setting, value: foundPosition }
          }
        }
        return setting
      }

      if (setting.key === 'Clockwise') {
        const rawClockwise = instruction.clockwise
        if (typeof rawClockwise === 'boolean') {
          return { ...setting, value: rawClockwise }
        }
        return setting
      }

      if (setting.key === 'TimeBetweenHeaters') {
        if (instruction.timeBetweenHeaters === 0)
          setUnpinnedHeater(true)
        return setting
      }

      if (setting.key === 'TimeForServoToTurnOneDegree') {
        const oneDegreeMs = Number(instruction.timeForServoToTurnOneDegree ?? 0)
        const pos = Number(instruction.swipeAnglePos ?? 45)
        const neg = Number(instruction.swipeAngleNeg ?? -45)
        const span = Math.abs(neg) + pos
        const totalSwipeSeconds = span > 0 ? (oneDegreeMs * span) / 1000 : 0
        return { ...setting, value:  Math.round(totalSwipeSeconds) }
      }

      const fieldName = instructionFieldBySettingKey[setting.key as Exclude<RecipeSetting['key'], 'Position' | 'Clockwise'>]
      const rawValue = instruction[fieldName]

      if (typeof rawValue === 'number') {
        return { ...setting, value: rawValue }
      }

      if (typeof rawValue === 'string' && rawValue.trim() !== '' && !Number.isNaN(Number(rawValue))) {
        return { ...setting, value: Number(rawValue) }
      }

      return setting
    })

    return {
      ...step,
      configuration,
    }
  }

  const getRecipeInstructions = async (): Promise<RecipeInstructionsResponse> => {
    const fetchedRecipe = await FetchFromBackoffice<RecipeInstructionsResponse>(`recipes/instructions/${recipeId}`)

    return {
      ...fetchedRecipe,
      instructions: fetchedRecipe.instructions ?? [],
    }
  }

  useEffect(() => {
    if (!isEditingExistingRecipe || !recipeId) {
      return
    }

    setIsFetchingRecipe(true)

    getRecipeInstructions()
      .then(fetchedRecipe => {
        setRecipeName(fetchedRecipe.name)

        const loadedSteps = fetchedRecipe.instructions
          .map(instruction => parseInstructionToRecipeStep(instruction))
          .filter((step): step is RecipeStep => step !== null)

        setAddedRecipes(loadedSteps)
      })
      .catch(error => {
        console.error('Could not fetch recipe instructions.', error)
        setErrorMessage('Could not fetch recipe instructions.')
      })
      .finally(() => {
        setIsFetchingRecipe(false)
      })
  
  }, [recipeId, isEditingExistingRecipe])

  const config = useMemo(() => ({
    FULL_SPEED: 7,
    ONE_LAP_FULL_SPEED_MS: 1880,
    GENERAL_SPEED: 3,
    compressorStartDelay: 500
  } as RuntimeEstimatorConfig), [])

  const estimated = useMemo(() => {
    const result = estimateRecipeRuntime(addedRecipes, config)

    const perStep = new Map(
      (result?.recipeEstimates ?? []).map(e => [e.id, Math.ceil((e.ms ?? 0) / 1000)])
    )

    return {
      totalSeconds: Math.ceil((result?.totalMs ?? 0) / 1000),
      perStep
    }
  }, [addedRecipes, config])

  const getNumericSettingValue = (
    step: RecipeStep,
    key: Extract<RecipeSetting['key'], 'Speed' | 'Time'>
  ): number | null => {
    const setting = step.configuration.find(s => s.key === key)
    if (!setting || setting.value === null || typeof setting.value !== 'number') return null
    return setting.value
  }

  const shouldShowRotateSpeedWarning = (steps: RecipeStep[], stepIndex: number): boolean => {
    const step = steps[stepIndex]
    if (!step || step.type !== 'rotate') return false

    const currentSpeed = getNumericSettingValue(step, 'Speed')
    if (currentSpeed === null || currentSpeed > 2) return false

    const previousStep = steps[stepIndex - 1]
    if (!previousStep || previousStep.type !== 'rotate') return true

    const previousSpeed = getNumericSettingValue(previousStep, 'Speed')
    const previousDurationMs = getNumericSettingValue(previousStep, 'Time')

    const hasValidPreRotate =
      previousSpeed !== null &&
      previousDurationMs !== null &&
      previousSpeed >= 3 &&
      previousDurationMs >= 1000

    return !hasValidPreRotate
  }

  const shouldShowHeaterFanWarning = (steps: RecipeStep[], stepIndex: number): boolean => {
    const step = steps[stepIndex]
    if (!step || step.type !== 'heaters-start') return false

    const previousSteps = steps.slice(0, stepIndex)
    if (!previousSteps) 
      return true

    if (!previousSteps.some(step => step.type === 'fan-start'))
      return true

    return false
  }

  const updateRecipeSetting = (stepId: string, settingIndex: number, value: RecipeSetting['value']) => {
    setAddedRecipes(prev => prev.map(step => {
      if (step.id !== stepId) {
        return step
      }

      const nextSettings = step.configuration.map((setting, index) => {
        if (index !== settingIndex) {
          return setting
        }

        if (setting.key === 'Position') {
          return { ...setting, value: value as PositionSetting['value'] }
        }

        if (setting.key === 'Clockwise') {
          return { ...setting, value: Boolean(value) || false }
        }

        if (value === null) {
          return { ...setting, value: null }
        }

        if (typeof value === 'number' && !Number.isNaN(value)) {
          return { ...setting, value }
        }

        if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
          return { ...setting, value: Number(value) }
        }

        return setting
      })

      return {
        ...step,
        configuration: nextSettings,
      }
    }))
  }

  const readDragPayload = (event: React.DragEvent): DragPayload | null => {
    const payload = event.dataTransfer.getData('application/x-recipe-step')

    if (!payload) {
      return null
    }

    try {
      return JSON.parse(payload) as DragPayload
    } catch {
      return null
    }
  }

  

  const handleDragStart = () => setIsDragging(true)
  const handleDragEnd = () => {
    setIsDragging(false)
    setDragOverIndex(null)
  }

  const handleDropAt = (insertIndex: number) => (event: React.DragEvent) => {
    event.preventDefault()
    setDragOverIndex(null)
    setIsDragging(false)

    const payload = readDragPayload(event)
    if (!payload) {
      return
    }

    setAddedRecipes(prev => {
    if (payload.source === 'library') {
      const next = [...prev]

      if (payload.type === 'heaters-start') {
        let hasFanBefore = false

        for (let index = 0; index < insertIndex; index++) {
          if (next[index]?.type === 'fan-start')
            hasFanBefore = true
        }

        if (!hasFanBefore) {
          next.splice(insertIndex, 0, createRecipeStep('fan-start'))
          insertIndex += 1
        }
      }

      next.splice(insertIndex, 0, createRecipeStep(payload.type))
      return next
    }

      const sourceIndex = prev.findIndex(step => step.id === payload.stepId)
      if (sourceIndex === -1) {
        return prev
      }

      const next = [...prev]
      const [movingStep] = next.splice(sourceIndex, 1)
      const adjustedIndex = sourceIndex < insertIndex ? insertIndex - 1 : insertIndex
      next.splice(adjustedIndex, 0, movingStep)
      return next
    })
  }

  const handleRemoveRecipe = (id: string) => {
    setAddedRecipes(prev => prev.filter(r => r.id !== id))
  }

  const handleDragOverAt = (index: number) => (event: React.DragEvent) => {
    event.preventDefault()
    setDragOverIndex(index)
  }

  const handleDragLeave = () => {
    setDragOverIndex(null)
  }

  const parseRecipe = (calibration: boolean = false, calibrationSuffix: string = "__calibration__") => {

    const firstFanStepIdx = addedRecipes.findIndex(r => r.type === 'fan-start')
    const firstHeaterStepIdx = addedRecipes.findIndex(r => r.type === 'heaters-start')

    const hasHeater = firstHeaterStepIdx !== -1
    const hasFanBeforeHeater = firstFanStepIdx !== -1 && firstFanStepIdx < firstHeaterStepIdx

    if (hasHeater && !hasFanBeforeHeater) {
      setSuccessMessage(null)
      setErrorMessage('There must exist a start fan step before the first start heater step!')
      return null
    }

    for (const step of addedRecipes) {
      const nullNumberSetting = step.configuration.find(setting =>
        setting.key !== 'Position' &&
        setting.key !== 'Clockwise' &&
        setting.value === null
      )

      if (nullNumberSetting) {
        const settingTitle = recipeSettingDetails[nullNumberSetting.key].title
        setSuccessMessage(null)
        setErrorMessage(`Please fill in \"${settingTitle}\" in step \"${step.title}\".`)
        return null
      }
    }

    const recipeInstructions = [
      {
        Id: "spray-setup"
      }
    ]

    addedRecipes.forEach((r) => {
      const configurationObject = Object.fromEntries(
        r.configuration.map((setting) => {
          if (setting.key === 'Position')
            return [setting.key, positionLookup[setting.value]]
          else if (setting.key === 'TimeForServoToTurnOneDegree') {
            const swipeAnglePos = r.configuration.find(setting => setting.key === 'SwipeAnglePos')?.value
            const swipeAngleNeg = r.configuration.find(setting => setting.key === 'SwipeAngleNeg')?.value
            const swipeAngleSpan = Math.abs(swipeAngleNeg!) + swipeAnglePos!
            const oneDegreeMilliseconds = setting.value! * 1000 / swipeAngleSpan
            return [setting.key, Math.round(oneDegreeMilliseconds)]
          }
          else if (setting.key === 'TimeBetweenHeaters') {
            const heaterDuration = r.configuration.find(setting => setting.key === 'TimeToRunHeaters')?.value
            return [setting.key, unpinnedHeater ? 0 : Math.round(heaterDuration! / 2)]
          }
          return [setting.key, setting.value]
        })
      )

      recipeInstructions.push({
        Id: r.type,
        ...configurationObject,
      })
    })

    let lastSprayIndex = -1
    for (let i = recipeInstructions.length - 1; i >= 0; i -= 1) {
      const id = recipeInstructions[i]?.Id
      if (id === 'spray-rotate' || id === 'spray-swipe') {
        lastSprayIndex = i
        break
      }
    }

    const stepToInsert = {
      Id: 'spray-finilize'
    }

    if (lastSprayIndex !== -1) {
      recipeInstructions.splice(lastSprayIndex + 1, 0, stepToInsert)
    }

    recipeInstructions.push({
      Id: "fan-stop"
    })

    const recipeInstructionsFinalized = {
      Id: calibration ? recipeName + calibrationSuffix : recipeName,
      Name: calibration ? recipeName + calibrationSuffix : recipeName,
      ImagePath: "textile.png",
      total_execution_time_ms: calibratedRecipeDuration * 1000,
      Version: 2,
      Instructions: recipeInstructions
    }

    return recipeInstructionsFinalized
  }

  const handleFetchCalibrationStatus = () => {

    if (calibrationArticleNumber && calibrationSerialNumber && calibrationPushedAt) {
      setIsFetchingCalibrationStatus(true)
      dispatch(fetchCalibrationStatus({ articleNumber: calibrationArticleNumber, serialNumber: calibrationSerialNumber, pushedAt: calibrationPushedAt }))
        .unwrap()
        .then(res => {
          if (!res.endedAt) {
            setSuccessMessage(`Found calibration run! It started ${res.duration} seconds ago, but has not yet been reported as completed.`)
            setErrorMessage(null)
          } else {
            setSuccessMessage('Found calibration run! Total duration was ' + res.duration + ' seconds. This duration will be used when saving the recipe or pushing the recipe to the hub.')
            setCalibratedRecipeDuration(res.duration)
            setErrorMessage(null)
          }
        })
        .catch((error) => {
          setSuccessMessage(null);
          setErrorMessage('Could not find calibration status! Run the calibration recipe first and then wait a little while until fetching duration.');
        })
        .finally(() => setIsFetchingCalibrationStatus(false))
    } else {
      setSuccessMessage(null)
      setErrorMessage('No calibration recipe sent!')
    }
  }

  const handlePushCalibrationRecipe = () => {

    const calibrationSuffix = "__calibration__"

    setPushedCalibration(false)

    if (!recipeName && !serialNumber) {
      setSuccessMessage(null)
      setErrorMessage('Please fill in recipe name and serial number.')
      return
    } else if (!recipeName) {
      setSuccessMessage(null)
      setErrorMessage('Please fill in the recipe name.')
      return
    } else if (!serialNumber) {
      setSuccessMessage(null)
      setErrorMessage('Please fill in the serial number.')
      return
    }

    const recipePayload = parseRecipe(true, calibrationSuffix)

    if (!recipePayload)
      return

    setIsPushingCalibration(true)

    dispatch(pushRecipe({ serialNumber: serialNumber!, recipe: recipePayload }))
      .then(res => {
        if (res.meta.requestStatus === 'rejected') {
          setSuccessMessage(null)
          setErrorMessage('Recipe could not be pushed to the hub, try again later.')
        } else {
          setSuccessMessage('Successfully pushed the calibration recipe! You may now start the recipe.')
          setErrorMessage(null)
          setPushedCalibration(true)
          setCalibrationSerialNumber(serialNumber)
          setCalibrationArticleNumber(recipeName + calibrationSuffix)
          setCalibrationPushedAt(DateTime.utc().toISO())
        }
        setIsPushingCalibration(false)
      })
  }

  const handlePushRecipe = () => {

    if (!recipeName && !serialNumber) {
      setSuccessMessage(null)
      setErrorMessage('Please fill in recipe name and serial number.')
      return
    } else if (!recipeName) {
      setSuccessMessage(null)
      setErrorMessage('Please fill in the recipe name.')
      return
    } else if (!serialNumber) {
      setSuccessMessage(null)
      setErrorMessage('Please fill in the serial number.')
      return
    }

    const recipePayload = parseRecipe()

    if (!recipePayload)
      return

    setIsPushing(true)

    dispatch(pushRecipe({ serialNumber: serialNumber!, recipe: recipePayload }))
      .then(res => {
        if (res.meta.requestStatus === 'rejected') {
          setSuccessMessage(null)
          setErrorMessage('Recipe could not be pushed to the hub, try again later.')
        } else {
          setSuccessMessage('Successfully pushed the recipe!')
          setErrorMessage(null)
        }
        setIsPushing(false)
      })
  }

  const handleSaveRecipe = () => {

    if (!recipeName) {
      setSuccessMessage(null)
      setErrorMessage('Please fill in the recipe name.')
      return
    }


    if (recipeName === "BaselineTemplate") {
      setSuccessMessage(null)
      setErrorMessage('You can not edit the baseline template! Please change the name to save a new recipe.')
      return
    }

    setIsSaving(true)
    setSaved(false)

    const recipePayload = parseRecipe()

    if (!recipePayload) {
      setIsSaving(false)
      return
    }
      
    dispatch(saveRecipe({ recipe: recipePayload }))
      .then(res => {
        if (res.meta.requestStatus === 'rejected') {
          setSuccessMessage(null)
          setErrorMessage('Recipe could not be saved, try again later.')
        } else {
          setSaved(true)
        }
        setIsSaving(false)
      })
  }

  const DefaultBlock = ({ name } : { name: string }) => {
    return (
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1.25,
          height: 70,
          borderRadius: '8px',
          backgroundColor: '#EEF2F4',
          border: '1px solid #DFE3E5',
          gap: 2
        }}
      >
        <Lock sx={{ color: styling.gray }}/>
        <Typography
          variant="body1"
          sx={{
            lineHeight: 1,        
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            fontWeight: '600'
          }}
        >
          {name}
        </Typography>
                </Paper>
    )
  }

  type BlockDividerProps = {
    index: number
    dragOverIndex: number | null
    isDragging: boolean
    handleDragOverAt: (index: number) => (event: React.DragEvent) => void
    handleDropAt: (index: number) => (event: React.DragEvent) => void
    handleDragLeave: () => void
  }

  const BlockDivider = ({
    index,
    dragOverIndex,
    isDragging,
    handleDragOverAt,
    handleDropAt,
    handleDragLeave,
  }: BlockDividerProps): React.ReactElement => (
    <Box
      onDragOver={handleDragOverAt(index)}
      onDrop={handleDropAt(index)}
      onDragLeave={handleDragLeave}
      sx={{
        my: 0.5,
        height: dragOverIndex === index ? 36 : 14,
        borderRadius: '8px',
        backgroundColor:
          dragOverIndex === index
            ? 'primary.light'
            : isDragging
              ? '#e2f2ea'
              : 'transparent',
        border: dragOverIndex === index ? '1px dashed #35B27D' : '1px dashed transparent',
        transition: 'all 0.15s ease',
      }}
    />
  )

  return (
    <Page title={isEditingExistingRecipe ? 'Edit Recipe' : 'Create New Recipe'}>
      <Paper sx={{
        display: 'flex',
        gap: 2,
        width: '100%',
        p: 2,
        marginBottom: 7
      }}>
        <Paper
          elevation={0}
          sx={{ p: 2, width: '70%' }}
        >
            <Box mb={2} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
                  <Typography variant="h5">Recipe</Typography>
                  {isFetchingRecipe && (<CircularProgress size={30}/>)}
                </Box>
                
                <Tooltip title={isSaving ? 'Saving...' : saved ? 'Saved' : 'Save'}>
                  <span>
                    <IconButton
                      onClick={() => handleSaveRecipe()}
                      disabled={isSaving || isFetchingRecipe}
                      aria-label={isSaving ? 'Saving' : 'Save'}
                      sx={{ p: 0, m: 0, lineHeight: 0 }}
                    >
                      {isSaving ? (
                        <CircularProgress size={18} />
                      ) : saved ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Save/>
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                    <Typography variant='body1'>Recipe name</Typography>
                    <TextField
                        placeholder='Enter recipe name'
                        size='small'
                        value={recipeName ?? ''}
                        onChange={event => setRecipeName(event.target.value)}
                        type='text'
                        disabled={isFetchingRecipe}
                        sx={{
                            width: '264px',
                            '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',           
                            },
                        }}
                    />
                </Box>
                <Box>
                    <Typography variant='body1'>Hub serial number</Typography>
                    <TextField
                        placeholder='Enter serial number'
                        size='small'
                        value={serialNumber ?? ''}
                        onChange={event => setSerialNumber(event.target.value)}
                        type='text'
                        disabled={isFetchingRecipe}
                        sx={{
                            width: '264px',
                            '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',           
                            },
                        }}
                    />
                </Box>
            </Box>

            <Box sx={{ mt: 4 }}>
              <DefaultBlock name={'Start'}/>
              <BlockDivider
                index={0}
                dragOverIndex={dragOverIndex}
                isDragging={isDragging}
                handleDragOverAt={handleDragOverAt}
                handleDropAt={handleDropAt}
                handleDragLeave={handleDragLeave}
              />
              {addedRecipes.map((step, index) => { 
                const Icon = step.icon
                const title = step.type === 'spray-swipe' ? 'Spray on ' + step.configuration.find(c => c.key === 'Position')?.value.toLowerCase() : step.title
                const showRotateWarning = shouldShowRotateSpeedWarning(addedRecipes, index)
                const showHeaterWarning = shouldShowHeaterFanWarning(addedRecipes, index)
                return (
                  <React.Fragment key={step.id}>
                    <Paper
                      draggable
                      onDragStart={(event) => {
                        handleDragStart()
                        event.dataTransfer.setData('application/x-recipe-step', JSON.stringify({ source: 'recipe', stepId: step.id }))
                      }}
                      onDragEnd={handleDragEnd}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        minHeight: { xs: 124, md: 86 },
                        borderRadius: '10px',
                        border: '1px solid #DFE3E5',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                        rowGap: 1.25,
                        backgroundColor: '#FFFFFF',
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          flex: '1 1 auto',
                          minWidth: 280,
                        }}
                      >
                        <DragIndicator sx={{ width: 24, height: 24, color: styling.gray }}/>
                        <Icon fontSize='medium' sx={{ color: styling.secondaryColor }}/>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Box>
                            <Typography variant='body1' fontWeight={600}>{title}</Typography>
                            <Typography variant='body2' fontWeight={200}>{`~${estimated?.perStep.get(step.id) ?? 0}s`}</Typography>
                          </Box>
                          <Box>
                            {showRotateWarning ? (
                              <Tooltip title='A rotate step with a speed of 2 or lower needs another rotate step right above it that has a speed of 3 or higher and lasts for more than one second'>
                                <WarningAmberOutlined sx={{ width: 20, height: 20, color: '#ED6C02' }} />
                              </Tooltip>
                            ) : null}
                            {showHeaterWarning ? (
                              <Tooltip title='A heater step must have a start fan step before it'>
                                <WarningAmberOutlined sx={{ width: 20, height: 20, color: '#ED6C02' }} />
                              </Tooltip>
                            ) : null}
                          </Box>
                      </Box>

                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          justifyContent: 'flex-end',
                          width: { xs: '100%', md: 'auto' },
                          mt: { xs: 0.5, md: 0 },
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          flexWrap: 'wrap',
                          justifyContent: 'flex-end',
                          width: { xs: '100%', md: 'auto' },
                          mt: { xs: 0.5, md: 0 },
                         }}
                        >
                          {step.type === 'heaters-start' ? (
                            <Box sx={{ maxHeight: 46, minWidth: 90 }}>
                              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
                                <Typography sx={{ lineHeight: 1 }} variant='body2' color={styling.secondaryColor}>{'Unpinned heater'}</Typography>
                                <Tooltip title={'Are the heaters of the machine(s) that will run this recipe unpinned or not'}>
                                  <InfoOutlined sx={{ width: 16, height: 16, color: styling.secondaryColor}}/>
                                </Tooltip>
                              </Box>

                              <Checkbox
                                checked={unpinnedHeater}
                                onChange={(event) =>
                                  setUnpinnedHeater(event.target.checked)
                                }
                                size='small'
                                sx={{
                                  p: 0,
                                  '& .MuiSvgIcon-root': { fontSize: 28 }
                                }}
                              />
                            </Box>
                          ) : null}
                          {step.configuration.map((setting, settingIndex) => {
                            const settingTitle = recipeSettingDetails[setting.key]?.title
                            const settingExplanation = recipeSettingDetails[setting.key]?.explanation
                            const isTimeInMilliseconds = ['Time', 'TimeToRunHeaters'].includes(setting.key) 
                            const isSpeed = setting.key === 'Speed'
                            const isSwipeDegree = setting.key === 'SwipeAngleNeg' || setting.key === 'SwipeAnglePos'
                            const min = recipeSettingDetails[setting.key]?.min ?? undefined
                            const max = isSpeed ? step.type === 'rotate' || step.type === 'spray-rotate' ? 7 : 3 : recipeSettingDetails[setting.key]?.max
                            const dontShow = ['TimeBetweenHeaters'].includes(setting.key) // This will be calculated as half of TimeToRunHeaters during parsing
                            return (
                              dontShow ? (
                                null
                              ) : 
                              isSwipeDegree ? (
                                <Box sx={{ maxWidth: 160, minWidth: 90 }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
                                    <Typography sx={{ lineHeight: 1 }} variant='body2' color={styling.secondaryColor}>{settingTitle}</Typography>
                                    <Tooltip title={settingExplanation}>
                                      <InfoOutlined sx={{ width: 16, height: 16, color: styling.secondaryColor}}/>
                                    </Tooltip>
                                  </Box>
            
                                  <TextField
                                    key={`${step.id}-${setting.key}-${settingIndex}`}
                                    size='small'
                                    type='number'
                                    value={setting.value === null
                                      ? ''
                                      : setting.key === 'SwipeAngleNeg' && Number(setting.value) < 0
                                      ? -Number(setting.value)
                                      : setting.value
                                    }
                                    inputProps={{
                                      min,
                                      max,
                                      step: 1,
                                    }}
                                    onChange={event => {
                                      const rawValue = event.target.value
                                      if (rawValue === '') {
                                        updateRecipeSetting(step.id, settingIndex, null)
                                        return
                                      }

                                      const raw = Number(rawValue)
                                      if (Number.isNaN(raw)) {
                                        return
                                      }

                                      const res = setting.key === 'SwipeAngleNeg' && raw > 0 
                                        ? -raw
                                        : raw

                                      updateRecipeSetting(step.id, settingIndex, res as RecipeSetting['value'])
                                    }}
                                    sx={{
                                      width: 70,
                                      '& .MuiOutlinedInput-root': {
                                        height: 30,
                                        borderRadius: '6px',
                                        fontSize: 13,
                                        minWidth: 80
                                      },
                                      '& .MuiSelect-select': {
                                        py: 0.5,
                                        display: 'flex',
                                        alignItems: 'center'
                                      },
                                    }}
                                  />
                                </Box>
                              ) : setting.key === 'Position' ? (
                                <Box sx={{ maxWidth: 160, minWidth: 90 }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
                                    <Typography sx={{ lineHeight: 1 }} variant='body2' color={styling.secondaryColor}>{settingTitle}</Typography>
                                    <Tooltip title={settingExplanation}>
                                      <InfoOutlined sx={{ width: 16, height: 16, color: styling.secondaryColor}}/>
                                    </Tooltip>
                                  </Box>
                                  <TextField
                                    key={`${step.id}-${setting.key}-${settingIndex}`}
                                    select
                                    size="small"
                                    value={setting.value}
                                    onChange={event => updateRecipeSetting(step.id, settingIndex, event.target.value as RecipeSetting['value'])}
                                    sx={{
                                      maxWidth: 98,
                                      '& .MuiOutlinedInput-root': {
                                        height: 30,
                                        borderRadius: '6px',
                                        fontSize: 13,
                                        minWidth: 80
                                      },
                                      '& .MuiSelect-select': {
                                        py: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                      },
                                    }}
                                  >
                                    {positionValues.map(position => (
                                      <MenuItem value={position}>{position}</MenuItem>
                                    ))}
                                  </TextField>
                                </Box>
                              ) : typeof setting.value === 'boolean' ? (
                                <Box sx={{ maxHeight: 46, minWidth: 90 }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
                                    <Typography sx={{ lineHeight: 1 }} variant='body2' color={styling.secondaryColor}>{settingTitle}</Typography>
                                    <Tooltip title={settingExplanation}>
                                      <InfoOutlined sx={{ width: 16, height: 16, color: styling.secondaryColor}}/>
                                    </Tooltip>
                                  </Box>

                                  <Checkbox
                                    checked={setting.value}
                                    onChange={(event) =>
                                      updateRecipeSetting(step.id, settingIndex, event.target.checked as RecipeSetting['value'])
                                    }
                                    size='small'
                                    sx={{
                                      p: 0,
                                      '& .MuiSvgIcon-root': { fontSize: 28 }
                                    }}
                                  />
                                </Box>
                              ) : 
                              (
                                <Box sx={{ maxWidth: 160, minWidth: 90 }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
                                    <Typography sx={{ lineHeight: 1 }} variant='body2' color={styling.secondaryColor}>{settingTitle}</Typography>
                                    <Tooltip title={settingExplanation}>
                                      <InfoOutlined sx={{ width: 16, height: 16, color: styling.secondaryColor}}/>
                                    </Tooltip>
                                  </Box>
            
                                  <TextField
                                    key={`${step.id}-${setting.key}-${settingIndex}`}
                                    size='small'
                                    type='number'
                                    value={setting.value === null
                                      ? ''
                                      : isTimeInMilliseconds
                                      ? setting.value / 1000
                                      : setting.value
                                    }
                                    inputProps={{
                                      min,
                                      max,
                                      step: 1,
                                    }}
                                    onChange={event => {
                                      const rawValue = event.target.value
                                      if (rawValue === '') {
                                        updateRecipeSetting(step.id, settingIndex, null)
                                        return
                                      }

                                      const raw = Number(rawValue)
                                      if (Number.isNaN(raw)) {
                                        return
                                      }

                                      const normalized = isTimeInMilliseconds ? raw * 1000 : raw

                                      const maxRefactored = max && isTimeInMilliseconds ? max * 1000 : max
                                      const minRefactored = min && isTimeInMilliseconds ? min * 1000 : min
                                      
                                      const clamped =
                                        isSpeed
                                          ? Math.min(max!, Math.max(min!, normalized))
                                          : Math.min(maxRefactored!, Math.max(minRefactored!, normalized))
                                      updateRecipeSetting(step.id, settingIndex, clamped as RecipeSetting['value'])
                                    }}
                                    sx={{
                                      width: 70,
                                      '& .MuiOutlinedInput-root': {
                                        height: 30,
                                        borderRadius: '6px',
                                        fontSize: 13,
                                        minWidth: 80
                                      },
                                      '& .MuiSelect-select': {
                                        py: 0.5,
                                        display: 'flex',
                                        alignItems: 'center'
                                      },
                                    }}
                                  />
                                </Box>
                              )
                            )
                            })}
                        </Box>
                        <Box>
                          <Button onClick={() => handleRemoveRecipe(step.id)} sx={{ display:'flex', alignItems: 'center' }}>
                            <Delete sx={{ color: styling.secondaryColor }}/>
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                    <BlockDivider
                      index={index + 1}
                      dragOverIndex={dragOverIndex}
                      isDragging={isDragging}
                      handleDragOverAt={handleDragOverAt}
                      handleDropAt={handleDropAt}
                      handleDragLeave={handleDragLeave}
                    />
                  </React.Fragment>
              )})}

              <DefaultBlock name={'End'}/>
            </Box>

            <Box sx={{ mt: 4, display: 'flex', gap: 1.5, flexDirection: 'column' }}>
              <Box sx={{ mb: 2, display: 'flex', gap: 1.5 }}>
                <Typography>{'Estimated total runtime: ' + estimated.totalSeconds + 's'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, flexDirection: 'row', justifyContent: 'space-between' }}>
                <LoadingButton
                  loading={isPushing}
                  disabled={isFetchingRecipe}
                  onClick={() => handlePushRecipe()}
                  loadingPosition="start"
                  startIcon={<ArrowUpward />}
                  variant="contained">
                  {'Push recipe'}
                </LoadingButton>
                <Box sx={{ display: 'flex', gap: 1.5, flexDirection: 'row' }}>
                  <LoadingButton
                    loading={isPushingCalibration}
                    disabled={isFetchingRecipe}
                    onClick={() => handlePushCalibrationRecipe()}
                    loadingPosition="start"
                    startIcon={<SquareFoot />}
                    variant="contained">
                    {'Push calibration recipe'}
                    <Tooltip title={"Send the calibration recipe to the hub, run it, and wait briefly before clicking the fetch recipe duration button."}>
                      <InfoOutlined sx={{ marginLeft: 1, width: 16, height: 16, color: styling.white}}/>
                    </Tooltip>
                  </LoadingButton>
                  {pushedCalibration && (
                    <LoadingButton
                      loading={isFetchingCalibrationStatus}
                      disabled={isFetchingRecipe || isPushingCalibration}
                      onClick={() => handleFetchCalibrationStatus()}
                      loadingPosition="start"
                      startIcon={<ArrowDownward />}
                      variant="contained"
                    >
                      {'Fetch recipe duration'}
                    </LoadingButton>
                  )}
                </Box>
              </Box>
              <Collapse in={Boolean(successMessage)}>
                <Alert
                  severity="success"
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                    >
                    </IconButton>
                  }
                  sx={{ mb: 2 }}
                >
                  {successMessage}
                </Alert>
              </Collapse>
              <Collapse in={Boolean(errorMessage)}>
                <Alert
                  severity="error"
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                    >
                    </IconButton>
                  }
                  sx={{ mb: 2 }}
                >
                  {errorMessage}
                </Alert>
              </Collapse>
            </Box>

        </Paper>

        <Paper elevation={0} sx={{ p: 2, width: '30%' }}>
            <Box>
                <Typography variant="h6">Recipe steps</Typography>
                <Typography sx={{ color: styling.secondaryColor }} variant="subtitle2">Drag the step onto the recipe</Typography>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {libraryBlocks.map(block => (
                <Paper
                  key={block.type}
                  elevation={0}
                  draggable
                  onDragStart={(event) => {
                    handleDragStart()
                    event.dataTransfer.setData('application/x-recipe-step', JSON.stringify({ source: 'library', type: block.type }))
                  }}
                  onDragEnd={handleDragEnd}
                  sx={{
                    p: 1.25,
                    borderRadius: '8px',
                    backgroundColor: '#FFFFFF',
                    cursor: 'grab',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <DragIndicator sx={{ width: 24, height: 24, color: styling.gray }}/>
                  <Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant='body1' fontWeight={600}>{block.title}</Typography>
                      {block.type === 'heaters-start' ? (
                        <Tooltip title={'The heaters require the fan to be active, so make sure a "Start fan" step has been added before this step'}>
                          <InfoOutlined sx={{ width: 16, height: 16, color: styling.secondaryColor}}/>
                        </Tooltip>
                      ) : null}
                    </Box>
                    <Typography variant='body2' sx={{ color: styling.secondaryColor }}>{block.description}</Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
        </Paper>
      </Paper>
    </Page>
  )
}

export default RecipeLab
